import { z } from 'zod';

import { bidTimeoutSecondsSchema, callIntervalSecondsSchema } from './auction.ts';
import { playerRoleSchema } from './player.ts';

/**
 * Contratto dei messaggi che attraversano RabbitMQ: i **comandi** diretti al
 * processor e gli **eventi** che il processor pubblica verso il resto del
 * sistema.
 *
 * Ogni messaggio viaggia dentro un inviluppo con identità (`commandId` /
 * `eventId`), asta di appartenenza, attore e timestamp: sono i campi che
 * rendono possibili idempotenza, audit e isolamento fra aste. Il payload
 * specifico sta nel campo `command`; chi consuma valida sempre l'inviluppo
 * con questi schemi e scarta ciò che non riconosce.
 */

// ---------------------------------------------------------------------------
// Attore
// ---------------------------------------------------------------------------

/** Ridichiarato da Prisma (`ActorType`); parità verificata in `libs/database`. */
export const ACTOR_TYPES = ['ADMIN', 'PARTICIPANT', 'SYSTEM'] as const;

export type ActorTypeValue = (typeof ACTOR_TYPES)[number];

export const actorTypeSchema = z.enum(ACTOR_TYPES);

/**
 * Chi ha originato il messaggio.
 *
 * `SYSTEM` non ha identità (timer, recovery), quindi `id` è `null`; per gli
 * altri attori l'id è obbligatorio: un comando anonimo non è verificabile.
 */
export const actorSchema = z
  .object({
    type: actorTypeSchema,
    id: z.uuid().nullable(),
  })
  .refine((actor) => (actor.type === 'SYSTEM' ? actor.id === null : actor.id !== null), {
    message: 'Attore non valido: SYSTEM non ha id, ADMIN e PARTICIPANT lo richiedono',
  });

export type ActorRef = z.output<typeof actorSchema>;

export const SYSTEM_ACTOR: ActorRef = { type: 'SYSTEM', id: null };

// ---------------------------------------------------------------------------
// Incrementi ammessi
// ---------------------------------------------------------------------------

/**
 * Gli unici incrementi che il server accetta. Il prezzo risultante è sempre
 * calcolato dal server: il client invia l'incremento, mai il totale.
 */
export const ALLOWED_BID_INCREMENTS = [1, 3, 5, 20] as const;

export type BidIncrementValue = (typeof ALLOWED_BID_INCREMENTS)[number];

export const bidIncrementSchema = z.union([
  z.literal(1),
  z.literal(3),
  z.literal(5),
  z.literal(20),
]);

// ---------------------------------------------------------------------------
// Comandi
// ---------------------------------------------------------------------------

export const AUCTION_COMMAND_TYPES = [
  'START_AUCTION',
  'PAUSE_AUCTION',
  'RESUME_AUCTION',
  'STOP_AUCTION',
  'FINISH_AUCTION',
  'PLACE_BID',
  'RESTART_CURRENT_CALL',
  'ROLLBACK_TO_EVENT',
  'UNDO_PURCHASE',
  'ASSIGN_PLAYER',
  'START_ROLE',
  'UPDATE_TIMING',
] as const;

export type AuctionCommandType = (typeof AUCTION_COMMAND_TYPES)[number];

const startAuctionCommandSchema = z.object({ type: z.literal('START_AUCTION') });
const pauseAuctionCommandSchema = z.object({ type: z.literal('PAUSE_AUCTION') });
const resumeAuctionCommandSchema = z.object({ type: z.literal('RESUME_AUCTION') });
const stopAuctionCommandSchema = z.object({ type: z.literal('STOP_AUCTION') });

/**
 * L'asta è finita perché l'amministratore lo dichiara.
 *
 * Distinto da `STOP_AUCTION` per la ragione per cui esistono due stati
 * terminali: `STOPPED` è un'interruzione — qualcosa è andato storto, l'asta si
 * è fermata prima del dovuto — `FINISHED` è la conclusione normale, quella che
 * il motore raggiunge da sé quando la sequenza si esaurisce. Un amministratore
 * che chiude l'asta con degli invenduti in elenco sta concludendo, non
 * interrompendo, e il registro deve dirlo con la parola giusta.
 *
 * Gli effetti sono per il resto quelli dello stop, chiamata aperta compresa.
 */
const finishAuctionCommandSchema = z.object({ type: z.literal('FINISH_AUCTION') });

const placeBidCommandSchema = z.object({
  type: z.literal('PLACE_BID'),
  /** Il calciatore che il client crede in chiamata: il processor verifica. */
  playerId: z.uuid(),
  increment: bidIncrementSchema,
  /**
   * L'offerta corrente su cui il client ha cliccato, `null` se nessuna.
   * Con l'incremento dà la cifra letta sul pulsante, che è ciò che si offre:
   * il processor la rifiuta solo se non batte più il prezzo corrente.
   */
  expectedHighestBid: z.number().int().min(0).nullable(),
});

const restartCurrentCallCommandSchema = z.object({ type: z.literal('RESTART_CURRENT_CALL') });

/**
 * Riporta l'asta a una riga del registro.
 *
 * L'ancora è un **evento**, non una posizione della sequenza: il registro è
 * l'unica cosa che sa davvero in che ordine sono successe le cose, e che sa
 * distinguere due passaggi sullo stesso calciatore. Tutto ciò che è accaduto
 * dopo quella riga viene annullato; la riga indicata, e quello che ha
 * prodotto, restano.
 */
const rollbackToEventCommandSchema = z.object({
  type: z.literal('ROLLBACK_TO_EVENT'),
  /** `AuctionEvent.id` della riga a cui tornare. */
  eventId: z.uuid(),
});

const undoPurchaseCommandSchema = z.object({
  type: z.literal('UNDO_PURCHASE'),
  purchaseId: z.uuid(),
});

/**
 * Prezzo minimo di un'assegnazione manuale.
 *
 * Un calciatore assegnato a zero non esiste nel Fantacalcio: il minimo è un
 * fantamilione, come la più piccola offerta possibile. Il massimo non si
 * dichiara qui — è il budget residuo del fantallenatore, e lo conosce solo il
 * processor.
 */
export const MANUAL_ASSIGNMENT_MIN_AMOUNT = 1;

/**
 * Assegnazione manuale decisa dall'amministratore.
 *
 * È l'unico comando in cui il **prezzo arriva dal client**: non è un'offerta
 * che il server calcola, è una decisione dell'amministratore (un accordo preso
 * a voce, la correzione di un errore). Restano al processor tutte le
 * validazioni che contano — appartenenza all'asta, budget, slot di ruolo,
 * calciatore non già assegnato.
 */
const assignPlayerCommandSchema = z.object({
  type: z.literal('ASSIGN_PLAYER'),
  playerId: z.uuid(),
  participantId: z.uuid(),
  amount: z.number().int().min(MANUAL_ASSIGNMENT_MIN_AMOUNT),
});

/**
 * Porta l'asta su un reparto.
 *
 * È anche il comando di avvio: da `DRAFT`/`READY` fa partire l'asta dal reparto
 * indicato. A asta in corso la sposta, chiudendo senza esito la chiamata
 * aperta — i calciatori del reparto lasciato restano disponibili e tornano
 * quando la rotazione ci ripassa.
 */
const startRoleCommandSchema = z.object({
  type: z.literal('START_ROLE'),
  role: playerRoleSchema,
});

/**
 * Cambia i due tempi del motore a asta già avviata.
 *
 * Esiste come comando, e non come `PATCH` della configurazione, perché a asta
 * viva i tempi appartengono al motore: è lui a derivare scadenze e intervalli,
 * ed è l'unico che possa cambiarli sapendo cosa c'è armato. Il processor lo
 * accetta **solo a asta in pausa** e la modifica vale dalla chiamata
 * successiva: quella congelata riparte con il residuo che aveva.
 *
 * Entrambi i valori viaggiano sempre, anche quando ne cambia uno solo: il
 * client rimanda i due numeri che ha davanti, come per gli slot di ruolo del
 * REST, così non c'è nessun campo da indovinare.
 */
const updateTimingCommandSchema = z.object({
  type: z.literal('UPDATE_TIMING'),
  bidTimeoutSeconds: bidTimeoutSecondsSchema,
  callIntervalSeconds: callIntervalSecondsSchema,
});

export const auctionCommandSchema = z.discriminatedUnion('type', [
  startAuctionCommandSchema,
  pauseAuctionCommandSchema,
  resumeAuctionCommandSchema,
  stopAuctionCommandSchema,
  finishAuctionCommandSchema,
  placeBidCommandSchema,
  restartCurrentCallCommandSchema,
  rollbackToEventCommandSchema,
  undoPurchaseCommandSchema,
  assignPlayerCommandSchema,
  startRoleCommandSchema,
  updateTimingCommandSchema,
]);

export type AuctionCommand = z.output<typeof auctionCommandSchema>;

/**
 * Inviluppo di un comando.
 *
 * `commandId` è generato da chi origina il comando (il browser per le offerte)
 * e garantisce l'idempotenza: lo stesso `commandId` sulla stessa asta viene
 * eseguito una volta sola, qualunque sia il numero di retry o riconsegne.
 */
export const commandEnvelopeSchema = z.object({
  commandId: z.uuid(),
  auctionId: z.uuid(),
  /** ISO-8601, orologio di chi ha emesso il comando: solo diagnostica. */
  issuedAt: z.iso.datetime(),
  actor: actorSchema,
  command: auctionCommandSchema,
});

export type CommandEnvelope = z.output<typeof commandEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Eventi
// ---------------------------------------------------------------------------

/** Ridichiarato da Prisma (`AuctionEventType`); parità verificata in `libs/database`. */
export const AUCTION_EVENT_TYPES = [
  'AUCTION_CREATED',
  'AUCTION_CONFIGURED',
  'AUCTION_STARTED',
  'AUCTION_PAUSED',
  'AUCTION_RESUMED',
  'AUCTION_STOPPED',
  'AUCTION_FINISHED',
  'AUCTION_RESTARTED_FROM',
  'PLAYERS_IMPORTED',
  'SEQUENCE_GENERATED',
  'PARTICIPANT_ADDED',
  'PARTICIPANT_REMOVED',
  'PARTICIPANT_INVITED',
  'INVITATION_REVOKED',
  'INVITATION_CONSUMED',
  'PLAYER_CALLED',
  'CALL_RESTARTED',
  'BID_PLACED',
  'BID_REJECTED',
  'PLAYER_SOLD',
  'PLAYER_UNSOLD',
  'PURCHASE_REVERTED',
  /**
   * L'amministratore ha aperto una sessione **come** un fantallenatore.
   *
   * Chi può agire al posto di un altro deve lasciare traccia di averlo fatto:
   * senza questa riga, un'offerta impersonata sarebbe indistinguibile da una
   * legittima. In coda all'elenco perché un valore di enum PostgreSQL si
   * aggiunge solo in fondo.
   */
  'PARTICIPANT_IMPERSONATED',
  /**
   * L'amministratore ha assegnato un calciatore a mano, fuori dalla chiamata.
   *
   * Distinto da `PLAYER_SOLD` di proposito: un'aggiudicazione è l'esito di una
   * gara di offerte, questa è una decisione presa da una persona. Il registro
   * deve poter distinguere le due cose — è la prima domanda che si pone chi
   * rilegge l'asta contestando un prezzo.
   */
  'PLAYER_ASSIGNED',
  /**
   * L'amministratore ha portato l'asta su un altro reparto.
   *
   * Il passaggio di reparto normalmente è automatico (finiti i calciatori o
   * esauriti gli slot); questo evento è l'altro caso, quello deciso da una
   * persona, e spiega perché un reparto è stato lasciato con calciatori ancora
   * disponibili. In coda all'elenco perché un valore di enum PostgreSQL si
   * aggiunge solo in fondo.
   */
  'AUCTION_ROLE_SWITCHED',
  /**
   * L'amministratore ha eliminato la sequenza senza sostituirla.
   *
   * È il gesto che riapre la modifica dell'ordine di chiamata: finché una
   * sequenza esiste, modalità e lettere sono bloccate perché la sequenza è
   * stata prodotta esattamente da quei valori.
   */
  'SEQUENCE_DISCARDED',
] as const;

export type AuctionEventTypeValue = (typeof AUCTION_EVENT_TYPES)[number];

/**
 * Stato di un'offerta. `VOIDED` è l'offerta di un tentativo annullato: resta a
 * storico perché spiega un prezzo che altrimenti sembrerebbe uscito dal nulla.
 */
export const BID_STATUSES = ['ACCEPTED', 'VOIDED'] as const;

export type BidStatusValue = (typeof BID_STATUSES)[number];

export const bidStatusSchema = z.enum(BID_STATUSES);

export const auctionEventTypeSchema = z.enum(AUCTION_EVENT_TYPES);

/**
 * Inviluppo di un evento pubblicato dal processor.
 *
 * `eventId` permette ai consumatori (gateway, worker, browser) di scartare i
 * duplicati: RabbitMQ garantisce at-least-once, non exactly-once. `commandId`
 * correla l'evento al comando che lo ha causato, `null` per gli eventi
 * spontanei del motore (scadenza countdown).
 */
export const eventEnvelopeSchema = z.object({
  eventId: z.uuid(),
  auctionId: z.uuid(),
  type: auctionEventTypeSchema,
  /** ISO-8601, orologio del processor: è il tempo autorevole. */
  occurredAt: z.iso.datetime(),
  actor: actorSchema,
  commandId: z.uuid().nullable(),
  /** Contenuto specifico dell'evento; gli schemi puntuali arrivano col motore. */
  payload: z.record(z.string(), z.unknown()),
});

export type EventEnvelope = z.output<typeof eventEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Topologia RabbitMQ
// ---------------------------------------------------------------------------

/**
 * Nomi della topologia. Un'unica coda comandi per tutte le aste: la
 * serializzazione per asta è compito del processor (lock distribuito), non
 * della topologia — una coda per asta andrebbe creata e distrutta a runtime.
 */
export const RABBITMQ_TOPOLOGY = {
  /** Coda durevole dei comandi diretti al processor. */
  commandsQueue: 'auction.commands',
  /** Exchange topic degli eventi pubblicati dal processor. */
  eventsExchange: 'auction.events',
  /** Dead-letter exchange: raccoglie i comandi non processabili. */
  deadLetterExchange: 'auction.dlx',
  /** Coda dei comandi avvelenati, per ispezione manuale. */
  commandsDeadLetterQueue: 'auction.commands.dlq',
} as const;

/**
 * Routing key degli eventi: `auction.{auctionId}`.
 *
 * Un consumatore può ascoltare tutte le aste (`auction.#`) o una sola: è la
 * base dell'isolamento fra aste anche sul broker.
 */
export function auctionEventsRoutingKey(auctionId: string): string {
  return `auction.${auctionId}`;
}

export const ALL_AUCTION_EVENTS_BINDING = 'auction.#';
